import { useEffect, useState } from "react";
import { SelectWithFilter, TextField } from "../../../../utils/form";
import { useApi } from "../../../../utils/hooks";
import _ from "lodash";
import localStorageSlim from "localstorage-slim";
import config from "../../../../config";
import { useSearchParams } from "react-router-dom";
import { Button, Box } from "@mui/material";

const { key: cacheKey, ttl: cacheTtl } = config.healthInsurances.cache;

const CUSTOM_OPTION = "OTRA COBERTURA";

const useFetchHealthInsurances = () => {
  //TODO improve this (patients token required for self register flow)
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const api = useApi(token);
  const [healthInsurances, setHealthInsurances] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const cachedData = localStorageSlim.get(cacheKey);

    if (cachedData) {
      setHealthInsurances(cachedData);
      setIsLoading(false);
      return;
    }

    const data = await api.healthInsurances();
    setHealthInsurances(data);
    setIsLoading(false);

    localStorageSlim.set(cacheKey, data, { ttl: cacheTtl });
  })();
  }, []);

  return { healthInsurances, isLoading };
};

const CustomHealthInsuranceInput = ({ form }) => {
  const { control, setValue } = form;

  const handleBackToList = () => {
    setValue("healthInsurance", "");
    setValue("isCustomHealthInsurance", false);
  };

  const validateCustomHealthInsurance = (value) => !_.isEmpty(value);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <TextField
        control={control}
        required
        name="healthInsurance"
        label="Nombre de cobertura"
        fullWidth
        rules={{ required: true, validate: validateCustomHealthInsurance }}
      />
      <Button onClick={handleBackToList} size="small">
        ← Volver al listado
      </Button>
    </Box>
  );
};

const HealthInsuranceSelect = ({ form, allowCustom = true }) => {
  const { control, setValue, watch } = form;
  const { healthInsurances, isLoading } = useFetchHealthInsurances();
  const isCustomHealthInsurance = watch("isCustomHealthInsurance");

  const healthInsurancesNames = _(healthInsurances).map(({ name }) => _.toUpper(name)).uniq().value();

  const healthInsurancesDescriptionsMap = _(healthInsurances)
    .keyBy(({ name }) => _.toUpper(name))
    .mapValues(({ description }) => description)
    .value();

  if (allowCustom) {
    healthInsurancesNames.push(CUSTOM_OPTION);
  }

  const renderOption = (props, option) => {
    const description = healthInsurancesDescriptionsMap[option];
    return (
      <li {...props} key={option}>
        {option} {description ? `(${description})` : ""}
      </li>
    );
  };

  const filterOptions = (options, { inputValue }) => {
    const search = _.toLower(_.trim(inputValue));
    return options.filter((option) => {
      const name = _.toLower(option);
      const description = _.toLower(healthInsurancesDescriptionsMap[option] || "");
      return name.includes(search) || description.includes(search);
    });
  };

  const isFromOurList = (value) =>
    healthInsurancesNames
      .filter(name => name !== CUSTOM_OPTION)
      .some(
        name => _.toLower(_.trim(name)) === _.toLower(_.trim(value))
    );

  const validateHealthInsurance = (value) => {
    if (isLoading) return true;
    if (value === CUSTOM_OPTION) return true;
    return isFromOurList(value);
  };

  const handleSelectChange = (value) => {
    if (value === CUSTOM_OPTION) {
      setValue("healthInsurance", "");
      setValue("isCustomHealthInsurance", true);
    } else {
      setValue("isCustomHealthInsurance", false);
    }
  };

  if (isCustomHealthInsurance) {
    return (
      <CustomHealthInsuranceInput
      form={form}
      />
    );
  }

  return <SelectWithFilter
      control={control}
      required
      validate={validateHealthInsurance}
      name="healthInsurance"
      label="Obra social/prepaga"
      helperText="Sin el código de plan. Si es particular, seleccioná 'Particular'"
      list={healthInsurancesNames}
      loading={isLoading}
      freeSolo={false}
      onPicked={handleSelectChange}
      renderOption={renderOption}
      filterOptions={filterOptions}
    />
};

export default HealthInsuranceSelect;
